import moment from 'moment';
// Helpers
import { buildAudienceFilters } from './audienceFilters';
import { getEngagementRateValue } from './campaign';
import { getLocationsForApi } from './campaignEditModal';
// Types
import {
  SearchCampaignCriteriaInput,
  UserRole,
  CampaignTaskType,
  CampaignMediaType,
} from 'api/graphql-global-types';
import { SocialCampaignFormValues } from 'pages/Campaigns/components/SocialCampaignsEditModal/SocialCampaignsEditModal';
import { GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 } from 'api/campaignV2/types/GetCampaignsV2';

export const getSocialCampaignCommonFields = (
  values: SocialCampaignFormValues,
  isTikTok?: boolean
): SearchCampaignCriteriaInput => {
  const {
    userRole,
    budget,
    flatFeePerStore,
    genders,
    maxAge,
    minAge,
    followingFrom,
    followingTo,
    locations,
    hometownLocations,
    excludedLocations,
    sports,
    engagementRate,
    searchHashtags,
    audienceGender,
    audienceAgeRanges,
    audienceEthnicities,
    audienceLocations,
    audienceInterests,
  } = values;

  // a lot ifs because of Expression produces a union type that is too complex to represent.  TS2590, this is a workaround

  const commonFields: SearchCampaignCriteriaInput = {};

  if (userRole) {
    commonFields.roles = userRole.length
      ? userRole
      : [UserRole.Athlete, UserRole.ContentCreator];
  }
  if (budget) {
    commonFields.budget = Number(budget);
  }
  if (flatFeePerStore) {
    commonFields.flatFeePerStore = Number(flatFeePerStore);
  }
  if (genders) {
    commonFields.genders = genders;
  }
  if (maxAge) {
    commonFields.maxAge = Number(maxAge);
  }
  if (minAge) {
    commonFields.minAge = Number(minAge);
  }
  if (followingFrom && followingTo) {
    commonFields.socialFollowersRange = {
      from: Number(followingFrom),
      to: Number(followingTo),
    };
  } else if (followingFrom) {
    commonFields.socialFollowersRange = {
      from: Number(followingFrom),
    };
  } else if (followingTo) {
    commonFields.socialFollowersRange = {
      to: Number(followingTo),
    };
  }
  if (locations) {
    commonFields.locations = locations;
  }
  if (hometownLocations) {
    commonFields.hometownLocations = hometownLocations;
  }
  if (excludedLocations) {
    commonFields.excludedLocations = getLocationsForApi(excludedLocations);
  }
  const engagementRateValue = getEngagementRateValue(engagementRate);
  if (engagementRateValue !== null) {
    commonFields.engagementRateRange = {
      from: engagementRateValue,
      to: 1,
    };
  }
  if (searchHashtags) {
    commonFields.hashtagInputs = searchHashtags.map((hashtag) => ({
      name: hashtag.value,
    }));
  }
  if (sports) {
    commonFields.sports = sports;
  }

  const audienceFilters = buildAudienceFilters({
    audienceGender,
    audienceAgeRanges,
    audienceEthnicities,
    audienceLocations,
    audienceInterests,
    isTikTok,
  });

  if (audienceFilters) {
    commonFields.audienceFilters = audienceFilters;
  }

  return commonFields;
};

// Deliverable utility types
export type DeliverableStatus = 'OVERDUE' | 'UPCOMING';

export type DeliverableWithStatus = {
  status: DeliverableStatus;
  dueDate: moment.Moment | null;
} & Omit<
  GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2,
  'dueDate'
>;

export type ContentItem = {
  id: string;
  title: string;
  description: string;
  mediaType: CampaignMediaType;
  dueDate: moment.Moment | null;
  suggestedCaption?: string;
};

// Deliverable utility functions
export const getStatusText = (status: DeliverableStatus): string => {
  return status === 'OVERDUE' ? 'Due Now' : 'Upcoming';
};

export const isContentTask = (deliverable: DeliverableWithStatus): boolean => {
  return deliverable.type === CampaignTaskType.CONTENT;
};

export const getContentItems = (
  deliverable: DeliverableWithStatus
): ContentItem[] => {
  if (
    deliverable.type === CampaignTaskType.CONTENT &&
    deliverable.contentItems
  ) {
    return deliverable.contentItems
      .filter((item) => item.__typename === 'CampaignContentTask')
      .map((item) => {
        const contentItem = item as any;
        let dueDate: moment.Moment | null = null;

        if (contentItem.dueDate?.fixedDate) {
          dueDate = moment(contentItem.dueDate.fixedDate);
        } else if (contentItem.dueDate?.rangeFrom) {
          dueDate = moment(contentItem.dueDate.rangeFrom);
        }

        return {
          id: contentItem.id,
          title: contentItem.title || `Content ${contentItem.id}`,
          description: contentItem.description || '',
          mediaType: contentItem.mediaType || CampaignMediaType.VIDEO,
          dueDate,
          suggestedCaption: contentItem.suggestedCaption,
        };
      });
  }
  return [];
};

export const processDeliverables = (
  deliverables: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2[]
): DeliverableWithStatus[] => {
  const now = moment();

  return deliverables
    .map((deliverable) => {
      let dueDate: moment.Moment | null = null;

      // Get due date based on task type
      if (deliverable.type === CampaignTaskType.CONTENT) {
        // For content tasks, get due date from content items
        const contentItem = deliverable.contentItems?.find(
          (item) => item.__typename === 'CampaignContentTask' && item.dueDate
        );
        if (contentItem && 'dueDate' in contentItem && contentItem.dueDate) {
          dueDate = moment(
            contentItem.dueDate.fixedDate || contentItem.dueDate.rangeFrom
          );
        }
      } else {
        // For post tasks, get due date from main task
        if ('dueDate' in deliverable && deliverable.dueDate?.fixedDate) {
          dueDate = moment(deliverable.dueDate.fixedDate);
        } else if ('dueDate' in deliverable && deliverable.dueDate?.rangeFrom) {
          dueDate = moment(deliverable.dueDate.rangeFrom);
        }
      }

      const status: DeliverableStatus = dueDate
        ? dueDate.isBefore(now)
          ? 'OVERDUE'
          : 'UPCOMING'
        : 'UPCOMING';

      return {
        ...deliverable,
        status,
        dueDate,
      };
    })
    .sort((a, b) => {
      // Sort by status first (OVERDUE first), then by due date
      if (a.status !== b.status) {
        return a.status === 'OVERDUE' ? -1 : 1;
      }
      if (a.dueDate && b.dueDate) {
        return a.dueDate.isBefore(b.dueDate) ? -1 : 1;
      }
      return 0;
    });
};
